import * as io from 'io-ts';
import { Address, Name } from './Buyer';
import { DisclosuresAttribute } from './Disclosures';
import { Money } from './Money';
import { ScheduledPayment } from './PaymentAgreement';
import { LocalizedString } from './Locale';

export enum PaymentTypes {
  INSTALLMENTS = 'INSTALLMENTS',
  SPLITPAY = 'SPLITPAY',
  HYBRID_LINE_OF_CREDIT = 'HYBRID_LINE_OF_CREDIT',
}

export type PaymentType = keyof typeof PaymentTypes;

export const TermsIntervals = io.union([
  io.literal('MONTHLY'),
  io.literal('BIWEEKLY'),
]);
export type TermsIntervals = io.TypeOf<typeof TermsIntervals>;

const Terms = io.type({
  originalPrincipal: Money,
  interval: TermsIntervals,
  length: io.number,
  rate: io.number,
});

export type Terms = io.TypeOf<typeof Terms>;

const ShortCode = io.type({
  value: io.string,
  expiresAt: io.string,
});

export type ShortCode = io.TypeOf<typeof ShortCode>;

const PaymentAgreement = io.type({
  createdAt: io.string,
  expiresAt: io.string,
  id: io.string,
  paymentAmount: Money,
  status: io.string,
  terms: Terms,
  updatedAt: io.string,
});

export type PaymentAgreement = io.TypeOf<typeof PaymentAgreement>;

export const Offer = io.type({
  capacity: Money,
  createdAt: io.string,
  id: io.string,
  overflowCapacity: io.union([Money, io.undefined]),
  paymentAgreement: PaymentAgreement,
  paymentProduct: io.type({
    id: io.string,
    type: io.keyof(PaymentTypes),
  }),
  shortCode: io.union([ShortCode, io.undefined]),
  status: io.string,
  statusCodes: io.union([io.array(io.string), io.null]),
  updatedAt: io.string,
  schedule: io.type({
    installments: io.array(ScheduledPayment),
  }),
  interest: Money,
});

export type Offer = io.TypeOf<typeof Offer>;

export const OrderFulfillmentType = io.union([
  io.literal('DELIVERY'),
  io.literal('PICKUP'),
  io.literal('MULTIPLE'),
  io.literal(''),
]);

export type OrderFulfillmentType = io.TypeOf<typeof OrderFulfillmentType>;

export const OrderItemFulfillmentType = io.union([
  io.literal('DELIVERY'),
  io.literal('PICKUP'),
  io.literal(''),
]);

export type OrderItemFulfillmentType = io.TypeOf<
  typeof OrderItemFulfillmentType
>;

export const OrderItem = io.intersection([
  io.type({
    name: io.string,
    quantity: io.number,
    shippingCost: Money,
    shippingDescription: io.union([io.string, LocalizedString]),
    unitPrice: Money,
    unitTax: Money,
  }),
  io.partial({
    category: io.string,
    itemUrl: io.string,
    sku: io.string,
    shippingProvider: io.string,
    shippingTrackingNumber: io.string,
    shippingTrackingURL: io.string,
    fulfillmentType: OrderItemFulfillmentType,
  }),
]);

export type OrderItem = io.TypeOf<typeof OrderItem>;

export const PickupInformation = io.intersection([
  io.type({
    address: Address,
  }),

  io.partial({
    name: Name,
    email: io.string,
    phone: io.string,
  }),
]);

export type PickupInformation = io.TypeOf<typeof PickupInformation>;

export const Order = io.intersection([
  io.type({
    items: io.union([io.array(OrderItem), io.undefined]),
    subTotal: Money,
    totalDiscounts: Money,
    totalPrice: Money,
    totalShipping: Money,
    totalTax: Money,
  }),

  io.partial({
    fulfillmentType: OrderFulfillmentType,
    pickupInformation: PickupInformation,
  }),
]);

export type Order = io.TypeOf<typeof Order>;

const ShortContact = io.type({
  address: Address,
  email: io.string,
  name: Name,
  phone: io.string,
});

export enum APPLICATION_STATUS {
  APPROVED = 'APPROVED',
  ATTEMPTED_CHECKOUT = 'ATTEMPTED_CHECKOUT',
  ATTEMPTED_PREPARE_CHECKOUT = 'ATTEMPTED_PREPARE_CHECKOUT',
  CANCELLED = 'CANCELLED',
  CHECKOUT_COMPLETED = 'CHECKOUT_COMPLETED',
  CHECKOUT_PREPARED = 'CHECKOUT_PREPARED',
  CHECKOUT_EXTENDED = 'CHECKOUT_EXTENDED',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
  INELIGIBLE = 'INELIGIBLE',
  NEEDS_ACTION = 'NEEDS_ACTION',
  STARTED = 'STARTED',
  UPDATED = 'UPDATED',
}

const Extension = io.type({
  key: io.string,
  value: io.string,
});

const ClientInfo = io.type({
  type: io.string,
  value: io.string,
});

const ApplicationOptional = io.type({
  buyerShippingContactID: io.string,
  ipAddress: io.string,
  paymentAgreementID: io.union([io.string, io.undefined]),
  status: io.keyof(APPLICATION_STATUS),
  statusCodes: io.union([io.array(io.string), io.null]),
  experienceCodes: io.union([io.array(io.string), io.undefined]),
  transactionID: io.string,
  shippingContact: ShortContact,
  paymentMethodID: io.string,
  autoPaymentMethodID: io.string,
});

const ApplicationOptionalOutgoing = io.type({
  billingContact: ShortContact,
  buyerID: io.string,
  capacity: Money,
  createdAt: io.string,
  expiresAt: io.string,
  id: io.string,
  merchantID: io.string,
  programID: io.string,
  offers: io.union([io.array(Offer), io.undefined]),
  clientInfo: io.union([io.array(ClientInfo), io.undefined]),
  extensions: io.union([io.array(Extension), io.undefined]),
  retargetingURL: io.union([io.string, io.undefined]),
  site_id: io.union([io.string, io.undefined]),
  neuro_user_id: io.union([io.string, io.undefined]),
  parentApplicationID: io.union([io.string, io.undefined]),
});

const ApplicationRequiredOutgoing = io.type({
  order: Order,
  disclosures: DisclosuresAttribute,
});

export const Application = io.intersection([
  ApplicationOptionalOutgoing,
  ApplicationRequiredOutgoing,
  io.partial(ApplicationOptional.props),
]);

export const OutgoingApplication = io.intersection([
  ApplicationRequiredOutgoing,
  io.partial(ApplicationOptionalOutgoing.props),
]);

export const ApplicationsResponse = io.array(Application);

export type OutgoingApplication = io.TypeOf<typeof OutgoingApplication>;
export type Application = io.TypeOf<typeof Application>;
export type ApplicationsResponse = io.TypeOf<typeof ApplicationsResponse>;

export const DirectExperienceExpiredResponse = io.type({
  isExpired: io.boolean,
});
export type DirectExperienceExpiredResponse = io.TypeOf<
  typeof DirectExperienceExpiredResponse
>;
